// src/pages/frontend_guhso/SignInPage.js
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './AuthPages.css';
import { requestOneTimeCode, verifyOneTimeCode } from '../../services/oneTimeCodeService';
import { useAuth } from '../../contexts/AuthContext';

const SignInPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setAuthToken } = useAuth();
  const [email, setEmail] = useState('');
  const [requestedEmail, setRequestedEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeRequested, setCodeRequested] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewCode, setPreviewCode] = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const [subscriber, setSubscriber] = useState(null);
  const [verified, setVerified] = useState(false);
  const redirectTo = new URLSearchParams(location.search).get('redirect') || '/profile';

  const developerPreviewEnabled =
    (process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV) !== 'production';

  const parseExpiry = (value) => {
    if (!value) return null;

    const parsed = typeof value === 'number' ? value : Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const formatMinutesUntil = (timestamp) => {
    if (!timestamp) return null;

    const diffMs = timestamp - Date.now();
    if (!Number.isFinite(diffMs) || diffMs <= 0) return null;

    return Math.max(1, Math.round(diffMs / 60000));
  };

  const resolveSubscriberName = (record) => {
    if (!record) return '';

    const first = record.firstName || record.first_name || '';
    const last = record.lastName || record.last_name || '';
    const combined = `${first} ${last}`.trim();

    return combined || record.name || '';
  };

  const resolveSubscriberEmail = (record) => {
    if (!record) return '';

    return (
      record.email ||
      record.emailAddress ||
      record.email_address ||
      record.primaryEmail ||
      record.primary_email ||
      ''
    );
  };

  const subscriberName = resolveSubscriberName(subscriber);
  const subscriberEmail = resolveSubscriberEmail(subscriber) || requestedEmail;

  const resetFeedback = () => {
    setStatusMessage('');
    setErrorMessage('');
  };

  const handleRequestCode = async (e) => {
    e.preventDefault();
    resetFeedback();

    const trimmed = (email || '').trim();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    if (!emailOk) {
      setErrorMessage('Please enter a valid email so we know where to send the code.');
      return;
    }

    setIsSubmitting(true);
    try {
      const requestResult = await requestOneTimeCode(trimmed);

      setSessionToken(requestResult.sessionToken || '');
      setSubscriber(requestResult.subscriber || null);

      setRequestedEmail(trimmed);
      setCodeRequested(true);
      setVerified(false);
      setCode('');
      setPreviewCode(developerPreviewEnabled ? requestResult.code || '' : '');

      const expiresAt = parseExpiry(requestResult.expiresAt);
      const expiresInMinutes = formatMinutesUntil(expiresAt);
      const baseMessage =
        requestResult.message ||
        `We just generated a six-digit code for ${trimmed}.`;

      setStatusMessage(
        expiresInMinutes
          ? `${baseMessage} It expires in about ${expiresInMinutes} minute${
              expiresInMinutes > 1 ? 's' : ''
            }.`
          : baseMessage
      );
    } catch (err) {
      setErrorMessage(err?.userMessage || err?.message || 'We could not send the code right now. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    resetFeedback();

    if (!requestedEmail) {
      setErrorMessage('Request a code first so we can verify your email address.');
      return;
    }
    if (!/^[0-9]{6}$/.test(code)) {
      setErrorMessage('Please enter the 6-digit code we sent to your email.');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await verifyOneTimeCode(requestedEmail, code, sessionToken);
      if (result?.success) {
        setVerified(true);
        setStatusMessage(
          result.message || 'Success! Your one-time code was accepted and you are now signed in.'
        );
        setPreviewCode('');
        setCode('');
        setSessionToken(result.sessionToken || sessionToken || '');
        setSubscriber(result.subscriber || subscriber || null);
        if (result.token) {
          setAuthToken(result.token);
          navigate(redirectTo, { replace: true });
        }
      } else {
        setErrorMessage(result?.message || 'That code didn\'t work. Please try again or request a new one.');
      }
    } catch (err) {
      setErrorMessage(err?.userMessage || err?.message || 'Something went wrong while verifying your code.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUseDifferentEmail = () => {
    setCodeRequested(false);
    setRequestedEmail('');
    setPreviewCode('');
    setCode('');
    setVerified(false);
    setSessionToken('');
    setSubscriber(null);
    resetFeedback();
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <span className="badge">Member Access</span>
          <h1>Sign in with One-Time Code</h1>
          <p>Enter your membership email address and we’ll send you a secure one-time code.</p>
        </div>

        {statusMessage && (
          <div className="auth-alert auth-alert--success" role="status" aria-live="polite">
            {statusMessage}
          </div>
        )}
        {errorMessage && (
          <div className="auth-alert auth-alert--danger" role="alert" aria-live="assertive">
            {errorMessage}
          </div>
        )}

        <form className="auth-form" onSubmit={codeRequested ? handleSignIn : handleRequestCode} noValidate>
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="you@example.com"
              value={codeRequested ? requestedEmail : email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={codeRequested && !verified}
            />
          </div>

          {codeRequested && (
            <div className="form-group">
              <label htmlFor="code">One-Time Access Code</label>
              <input
                id="code"
                name="code"
                type="text"
                placeholder="Enter the 6-digit code"
                inputMode="numeric"
                pattern="^[0-9]{6}$"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                disabled={verified}
              />
              <p className="auth-highlight">
                Check your inbox for the code we just sent. It expires in five minutes to keep your account secure.
              </p>
              {developerPreviewEnabled && previewCode && (
                <p className="auth-preview">
                  <strong>Testing shortcut:</strong> {previewCode}
                </p>
              )}
            </div>
          )}

          <button
            className="primary-button"
            type="submit"
            disabled={isSubmitting || (verified && codeRequested)}
          >
            {isSubmitting
              ? 'Working…'
              : codeRequested
              ? verified
                ? 'Signed In'
                : 'Verify & Sign In'
              : 'Send Me the Code'}
          </button>

          {codeRequested && (
            <button
              className="secondary-button"
              type="button"
              onClick={handleUseDifferentEmail}
              disabled={isSubmitting}
            >
              Use a different email
            </button>
          )}
        </form>

        {verified && (
          <div className="auth-summary" role="status" aria-live="polite">
            <h2>All access granted</h2>
            <p>
              {subscriberName
                ? `Welcome back, ${subscriberName}!`
                : 'You are signed in and ready to enjoy exclusive content.'}
            </p>
            {subscriberEmail && (
              <p className="auth-secondary-text">Primary email on file: {subscriberEmail}</p>
            )}
            <p className="auth-secondary-text">
              Continue to <Link to={redirectTo}>your requested subscriber content</Link>.
            </p>
          </div>
        )}

        <div className="auth-footer">
          <span>
            Need a paid subscription? <Link to={`/register?redirect=${encodeURIComponent(redirectTo)}`}>Start here</Link>.
          </span>
          <span>
            Want to head out? <Link to="/signout">Sign out from all devices</Link>.
          </span>
        </div>
      </div>
    </div>
  );
};

export default SignInPage;
